import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AddressEditor, fmtAddress } from "@/components/routing/AddressEditor";
import { TagPicker } from "@/components/routing/TagPicker";
import { Plus, Pencil, Trash2, Search, Loader2, Accessibility, MapPin } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", email: "", addresses: [], service_tags: [], wheelchair: false, notes: "", status: "ACTIVE" };

export default function Customers() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("customers:manage");
  const [rows, setRows] = useState(null);
  const [tags, setTags] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/customers").then((r) => setRows(r.data)).catch((e) => { toast.error(apiError(e)); setRows([]); }), []);
  useEffect(() => { load(); api.get("/service-tags").then((r) => setTags(r.data.map((t) => t.name))).catch(() => {}); }, [load]);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    const addresses = form.addresses.map((a) => ({ ...a, lat: a.lat === "" || a.lat == null ? null : Number(a.lat), lng: a.lng === "" || a.lng == null ? null : Number(a.lng) }));
    setSaving(true);
    try {
      if (form.id) await api.put(`/customers/${form.id}`, { ...form, addresses });
      else form.id = (await api.post("/customers", { ...form, addresses })).data.id;
      const g = await api.post(`/customers/${form.id}/geocode`).then((r) => r.data).catch(() => null);
      if (g?.failed?.length) toast.warning(`${g.failed.length} address(es) could not be located on the map`);
      toast.success("Customer saved"); setForm(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Archive ${c.name}?`)) return;
    try { await api.delete(`/customers/${c.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const list = (rows || []).filter((r) => !q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="customers-page">
      <PageHeader title="Customers" subtitle="People we transport and support, with all their pickup and drop-off addresses."
        actions={canManage && <Button onClick={() => setForm({ ...EMPTY })} data-testid="customer-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Customer</Button>} />
      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" className="pl-9 bg-card" data-testid="customer-search" />
      </div>
      {rows === null ? <Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.length === 0 && <p className="text-muted-foreground text-sm">No customers yet.</p>}
          {list.map((c) => (
            <div key={c.id} className="border border-border rounded-lg bg-card p-4" data-testid="customer-card">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold flex items-center gap-1.5">{c.name}{c.wheelchair && <Accessibility className="w-4 h-4 text-brand" aria-label="Wheelchair" />}</p>
                  <p className="text-xs text-muted-foreground">{[c.phone, c.email].filter(Boolean).join(" · ") || "—"}</p>
                </div>
                <StatusBadge value={c.status || "ACTIVE"} />
              </div>
              <ul className="mt-3 space-y-1">
                {(c.addresses || []).map((a) => (
                  <li key={a.id} className="text-xs flex gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" /><span><b>{a.label || "Address"}</b>{a.is_default && " (default)"} — {fmtAddress(a) || "—"}</span></li>
                ))}
                {!(c.addresses || []).length && <li className="text-xs text-muted-foreground">No addresses</li>}
              </ul>
              <div className="flex flex-wrap gap-1 mt-3">{(c.service_tags || []).map((t) => <span key={t} className="text-[11px] rounded-full bg-muted px-2 py-0.5">{t}</span>)}</div>
              {canManage && (
                <div className="flex justify-end gap-1 mt-2">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setForm({ ...EMPTY, ...c })} data-testid="customer-edit"><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-danger" onClick={() => remove(c)} data-testid="customer-delete"><Trash2 className="w-4 h-4" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form?.id ? "Edit" : "New"} Customer</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3.5">
              <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone" /></div>
                <div className="space-y-1.5"><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email" /></div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={!!form.wheelchair} onCheckedChange={(v) => setForm({ ...form, wheelchair: v })} data-testid="customer-wheelchair" /><Label>Requires wheelchair-accessible vehicle</Label></div>
              <div className="space-y-1.5"><Label>Service tags</Label><TagPicker options={tags} value={form.service_tags || []} onChange={(v) => setForm({ ...form, service_tags: v })} testid="customer-tags" /></div>
              <div className="space-y-1.5"><Label>Addresses</Label><AddressEditor value={form.addresses || []} onChange={(v) => setForm({ ...form, addresses: v })} /></div>
              <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="customer-notes" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="customer-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
